import * as THREE from 'three';
import { OrbitControls } from 'three/addons/controls/OrbitControls.js';
import { CameraTransform } from '../scene/SceneConfig';

export type CameraMode = 'orbit' | 'follow' | 'cinematic' | 'free';

export interface CameraPresets {
  overview: CameraTransform;
  sunClose: CameraTransform;
  mempoolFocus: CameraTransform;
  blockOrbit: CameraTransform;
}

export class CameraController {
  public camera: THREE.PerspectiveCamera;
  public controls: OrbitControls;
  public mode: CameraMode = 'orbit';
  
  private _renderer: THREE.WebGLRenderer; // Reserved for future aspect ratio calculations
  private followTarget: THREE.Object3D | null = null;
  private cinematicAnimation: {
    startPos: THREE.Vector3;
    endPos: THREE.Vector3;
    startTarget: THREE.Vector3;
    endTarget: THREE.Vector3;
    duration: number;
    elapsed: number;
    active: boolean;
  } | null = null;
  
  public readonly viewPresets: CameraPresets = {
    overview: {
      position: new THREE.Vector3(0, 100, 200),
      target: new THREE.Vector3(0, 0, 0),
      fov: 75,
    },
    sunClose: {
      position: new THREE.Vector3(20, 10, 20),
      target: new THREE.Vector3(0, 0, 0),
      fov: 60,
    },
    mempoolFocus: {
      position: new THREE.Vector3(-50, 50, 100),
      target: new THREE.Vector3(-50, 0, 0),
      fov: 45,
    },
    blockOrbit: {
      position: new THREE.Vector3(80, 30, 80),
      target: new THREE.Vector3(50, 0, 0),
      fov: 65,
    },
  };
  
  constructor(renderer: THREE.WebGLRenderer, initialPosition?: THREE.Vector3) {
    this._renderer = renderer;
    
    // Create camera
    this.camera = new THREE.PerspectiveCamera(
      75,
      window.innerWidth / window.innerHeight,
      0.1,
      2000
    );
    
    if (initialPosition) {
      this.camera.position.copy(initialPosition);
    } else {
      this.camera.position.copy(this.viewPresets.overview.position);
    }
    
    // Create controls
    this.controls = new OrbitControls(this.camera, renderer.domElement);
    this.controls.enableDamping = true;
    this.controls.dampingFactor = 0.05;
    this.controls.enableZoom = true;
    this.controls.enableRotate = true;
    this.controls.enablePan = true;
    
    // Set reasonable limits
    this.controls.minDistance = 5;
    this.controls.maxDistance = 1000;
    this.controls.maxPolarAngle = Math.PI; // Allow full rotation
    
    // Set initial target
    this.controls.target.copy(this.viewPresets.overview.target);
    this.controls.update();
  }
  
  setMode(mode: CameraMode): void {
    this.mode = mode;
    
    switch (mode) {
      case 'orbit':
        this.controls.enabled = true;
        this.followTarget = null;
        this.cinematicAnimation = null;
        break;
        
      case 'follow':
        this.controls.enabled = false;
        this.cinematicAnimation = null;
        break;
        
      case 'cinematic':
        this.controls.enabled = false;
        this.followTarget = null;
        this.startCinematicMode();
        break;
        
      case 'free':
        this.controls.enabled = false;
        this.followTarget = null;
        this.cinematicAnimation = null;
        break;
    }
  }
  
  followObject(object: THREE.Object3D): void {
    this.followTarget = object;
    this.setMode('follow');
    
    // Store follow target info for state export
    if (object.userData?.['id']) {
      console.log(`Following object: ${object.userData['id']} (${object.userData['type'] || 'unknown'})`);
    }
  }

  
  stopFollowing(): void {
    if (this.followTarget) {
      console.log('Stopped following object');
      this.followTarget = null;
      this.setMode('orbit');
    }
  }

  getFollowTarget(): THREE.Object3D | null {
    return this.followTarget;
  }

  isFollowing(): boolean {
    return this.mode === 'follow' && this.followTarget !== null;
  }
  
  animateTo(position: THREE.Vector3, target: THREE.Vector3, duration: number = 2000): void {
    this.cinematicAnimation = {
      startPos: this.camera.position.clone(),
      endPos: position.clone(),
      startTarget: this.controls.target.clone(),
      endTarget: target.clone(),
      duration,
      elapsed: 0,
      active: true,
    };
    
    this.setMode('cinematic');
  }
  
  setPreset(presetName: keyof CameraPresets): void {
    const preset = this.viewPresets[presetName];
    this.animateTo(preset.position, preset.target);
    
    if (preset.fov) {
      this.camera.fov = preset.fov;
      this.camera.updateProjectionMatrix();
    }
  }
  
  update(deltaTime: number): void {
    switch (this.mode) {
      case 'orbit':
        this.controls.update();
        break;
        
      case 'follow':
        this.updateFollowMode();
        break;
        
      case 'cinematic':
        this.updateCinematicMode(deltaTime);
        break;
        
      case 'free':
        // Manual control mode - no automatic updates
        break;
    }
  }
  
  private updateFollowMode(): void {
    if (!this.followTarget) return;
    
    const targetPos = this.followTarget.position;
    const targetVelocity = this.followTarget.userData?.['velocity'] || new THREE.Vector3(0, 0, 0);
    
    // Calculate dynamic offset based on object movement
    const baseOffset = new THREE.Vector3(15, 10, 15);
    const velocityOffset = targetVelocity.clone().multiplyScalar(2); // Look ahead
    const dynamicOffset = baseOffset.clone().add(velocityOffset);
    
    // Calculate desired camera position
    const desiredPosition = targetPos.clone().add(dynamicOffset);
    
    // Smooth camera movement using lerp
    const lerpFactor = 0.05; // Adjust for smoothness (0.01 = very smooth, 0.1 = more responsive)
    this.camera.position.lerp(desiredPosition, lerpFactor);
    
    // Calculate look-at point slightly ahead of the target
    const lookAtPoint = targetPos.clone().add(velocityOffset.multiplyScalar(0.5));
    
    // Smooth camera rotation
    const currentLookAt = new THREE.Vector3();
    this.camera.getWorldDirection(currentLookAt);
    const targetDirection = lookAtPoint.clone().sub(this.camera.position).normalize();
    
    // Slerp the camera orientation for smooth rotation
    const currentQuaternion = this.camera.quaternion.clone();
    const targetQuaternion = new THREE.Quaternion().setFromRotationMatrix(
      new THREE.Matrix4().lookAt(this.camera.position, lookAtPoint, new THREE.Vector3(0, 1, 0))
    );
    
    this.camera.quaternion.slerp(targetQuaternion, lerpFactor);
  }
  
  private updateCinematicMode(deltaTime: number): void {
    if (!this.cinematicAnimation || !this.cinematicAnimation.active) return;
    
    this.cinematicAnimation.elapsed += deltaTime * 1000; // Convert to ms
    const progress = Math.min(this.cinematicAnimation.elapsed / this.cinematicAnimation.duration, 1.0);
    
    // Use easing function for smooth animation
    const easedProgress = this.easeInOutCubic(progress);
    
    // Interpolate position
    this.camera.position.lerpVectors(
      this.cinematicAnimation.startPos,
      this.cinematicAnimation.endPos,
      easedProgress
    );
    
    // Interpolate target
    this.controls.target.lerpVectors(
      this.cinematicAnimation.startTarget,
      this.cinematicAnimation.endTarget,
      easedProgress
    );
    
    this.controls.update();
    
    // End animation when complete
    if (progress >= 1.0) {
      this.cinematicAnimation.active = false;
      this.setMode('orbit'); // Return to orbit mode
    }
  }
  
  private startCinematicMode(): void {
    // Start a gentle orbit animation
    const radius = this.camera.position.distanceTo(this.controls.target);
    const height = this.camera.position.y;
    
    // Create circular path
    const angle = Math.atan2(this.camera.position.z, this.camera.position.x);
    const endAngle = angle + Math.PI * 0.5; // Quarter circle
    
    const endPos = new THREE.Vector3(
      Math.cos(endAngle) * radius,
      height,
      Math.sin(endAngle) * radius
    );
    
    this.animateTo(endPos, this.controls.target.clone(), 5000);
  }
  
  private easeInOutCubic(t: number): number {
    return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
  }
  
  onResize(width: number, height: number): void {
    this.camera.aspect = width / height;
    this.camera.updateProjectionMatrix();
  }
  
  dispose(): void {
    this.controls.dispose();
  }
  
  exportState() {
    return {
      position: this.camera.position.toArray() as [number, number, number],
      rotation: this.camera.rotation.toArray().slice(0, 3) as [number, number, number],
      fov: this.camera.fov,
      mode: this.mode,
      target: this.controls.target.toArray() as [number, number, number],
      followTarget: this.followTarget?.userData?.['id'] || null,
      cinematicActive: this.cinematicAnimation?.active || false,
    };
  }
}